<template>
  <SoDialog
    v-model="value"
    :loading="false"
    card-title="Renewal Summary"
    data-testid="renewal-summary-modal"
  >
    <SoErrorBoundary>
      <SoForm v-model="isValid">
        <section
          class="border rounded pa-4"
          data-testid="renewal-summary-new-agreements"
        >
          <h6 class="font-weight-medium">New Agreements</h6>
          <template v-for="tariff in newAgreements" :key="tariff.id">
            <SoDatePicker
              v-if="tariff.isGas"
              ref="gas-renew-datepicker"
              v-model="gasChangeDate"
              :label="tariff.display"
              :min-date="renewals.minRenewalDate"
              :max-date="renewals.maxRenewalDate"
            />
            <SoDatePicker
              v-else-if="!tariff.isGas"
              ref="elec-renew-datepicker"
              v-model="elecChangeDate"
              :label="tariff.display"
              :min-date="renewals.minRenewalDate"
              :max-date="renewals.maxRenewalDate"
            />
          </template>
          <SoAlert
            v-if="
              elecChangeDate?.toLocaleDateString() !==
                gasChangeDate?.toLocaleDateString() && newAgreements.length > 1
            "
            class="mt-5"
            type="warning"
          >
            The start dates for gas and electricity are different. Please make
            sure this is correct before continuing.
          </SoAlert>
        </section>

        <section
          class="border rounded pa-4 my-2"
          data-testid="renewal-summary-exit-fees"
        >
          <h6 class="font-weight-medium">Early Exit Fees</h6>
          <!-- SO-15714: it's a business requirement to keep this disabled -->
          <SoCheckbox
            v-model="exitFeesIncluded"
            disabled
            :label="
              renewals.exitFeeTotal === 0
                ? `(${formatMoney({
                    amount: renewals.exitFeeTotal,
                  })}) Exit Fees`
                : `Early Exit Fees ${formatMoney({
                    amount: renewals.exitFeeTotal,
                  })} ${exitFeesIncluded ? 'Applied' : 'Waived'}`
            "
          />
          <p
            v-if="showElecVatRateChangeInfo"
            class="text-body-2 text-medium-emphasis mt-1 mb-0"
          >
            Exit fees are applied on the next bill at the VAT rate in effect on
            the bill issue date.
          </p>
        </section>

        <section
          class="border rounded text-body-2 pa-4 my-2"
          data-testid="renewal-summary-payment-schedule"
        >
          <h6 class="font-weight-medium">New Payment Schedule</h6>
          <SoTextField
            v-model="newScheduleAmount"
            :rules="[rules.required, rules.greaterThanZero]"
            prefix="£"
            type="number"
            data-testid="renewals-schedule-amount"
            name="renewals-schedule-amount"
          />
          <SoCheckbox v-model="newScheduleIsSeasonal" label="Seasonal" />
          <SoSelect
            v-model="newScheduleDayOfMonth"
            :items="dayOfMonthOptions"
            :rules="[rules.required]"
            label="Day of month"
          />
          <SoDatePicker
            ref="schedule-from-datepicker"
            v-model="newScheduleFromDate"
            :min-date="new Date()"
            label="Payment schedule from date"
          />
          <h6 class="font-weight-medium mt-2">
            {{
              newScheduleIsSeasonal
                ? formatSeasonalAmount({
                    amount: Number(newScheduleAmount) || 0,
                    divider: "or",
                  })
                : formatMoney({ amount: Number(newScheduleAmount) })
            }}
          </h6>
          <p>
            Your first new payment will be collected on day
            {{ newScheduleDayOfMonth }} of the month, on or after
            {{ formatDate(paymentScheduleFromDate) }}. If that would fall in the
            same month as your final current payment, it moves to the following
            month.
          </p>
        </section>

        <section
          class="rounded bg-info text-body-2 pa-4 my-2"
          data-testid="renewal-summary-active-schedules"
        >
          <i v-if="!futureSchedules.data.length">
            No further payments will be collected on current payment schedule.
          </i>
          <template v-else>
            <p>
              New payment schedule will begin after current schedule has
              finished.
            </p>
            <template
              v-for="schedule in futureSchedules.data"
              :key="schedule.id"
            >
              <h2 class="my-1">
                {{
                  schedule.isSeasonal
                    ? schedule.amountFormatted
                    : formatMoney({ amount: schedule.amount })
                }}
              </h2>
              <SoChip :text="`${formatDay(schedule.nextPaymentDt)} of month`" />
              <SoChip
                :text="`Starts ${formatDate(schedule.fromDt)}`"
                class="ml-1"
              />
              <SoChip
                v-if="schedule.toDt && isAfterToday(schedule.fromDt)"
                :text="`Ends ${formatDate(schedule.toDt)}`"
                class="ml-1"
              />
            </template>
            <p v-if="currentSchedule" class="mt-2">
              Next payment of
              {{ formatMoney({ amount: currentSchedule.amount }) }} on
              {{ formatDate(currentSchedule.nextPaymentDt) }} ({{
                daysFromTodayFormatted(currentSchedule.nextPaymentDt)
              }})
            </p>
          </template>
        </section>

        <SoAlert
          data-testid="renewal-summary-cooling-off-date-alert"
          class="mb-2"
        >
          The 14 days cooling off period will start as of today. Inform the
          customer they have until {{ formatDate(coolingOffEndDate) }} to cancel
          this tariff change. After this date, early cancellation will incur
          exit fees.
          <div class="d-flex justify-end align-center">
            <label for="cooling-off-period-approved" class="mr-2 text--bold"
              >Confirm</label
            >
            <SoCheckbox
              id="cooling-off-period-approved"
              v-model="isCoolingOffPeriodApproved"
            />
          </div>
        </SoAlert>

        <SoAlert>
          Before renewing the customer, please confirm you have explained the
          following and the customer has clearly agreed to:
          <ul class="pl-2 renewal-summary-modal__customer-consent-list">
            <li>Unit Rate</li>
            <li>Standing Charge</li>
            <li>Tariff Name</li>
            <li>Tariff Length</li>
            <li>Estimated Annual Cost</li>
            <li>Direct Debit Amount</li>
            <li>Exit Fees</li>
            <li>VAT</li>
          </ul>
          <div class="d-flex justify-end align-center">
            <label for="renewal-consent" class="mr-2 text--bold">Confirm</label>
            <SoCheckbox
              id="renewal-consent"
              v-model="isCustomerConsentApproved"
            />
          </div>
        </SoAlert>

        <section
          class="d-flex align-center justify-space-between ga-4 rounded bg-surface-dim text-body-2 pa-4 my-2"
          data-testid="renewal-summary-actions"
        >
          <p class="mb-0" data-testid="renewal-summary-submit-explainer">
            Submitting the Renewal creates the New Agreements, Updates the
            Payment Schedule and sends the Welcome Pack
          </p>
          <SoTooltip
            :text="`Please confirm the ${
              !isCoolingOffPeriodApproved ? 'cooling off period' : 'consent'
            } message`"
            :disabled="isCoolingOffPeriodApproved && isCustomerConsentApproved"
          >
            <SoButton
              :loading="RenewAgreementMutator.isPending"
              :disabled="
                isValid === false ||
                !isCoolingOffPeriodApproved ||
                !isCustomerConsentApproved ||
                RenewAgreementMutator.isSuccess
              "
              :title="
                RenewAgreementMutator.isSuccess ? 'Submitted' : 'Submit Renewal'
              "
              data-testid="renewal-summary-submit-btn"
              @click="submitRenewal()"
            />
          </SoTooltip>
        </section>

        <SoAlert
          v-if="RenewAgreementMutator.isSuccess && paymentScheduleNotApplied"
          type="warning"
          class="mb-2"
          data-testid="renewal-summary-warning"
        >
          <div class="d-flex align-center justify-space-between ga-4">
            <span>
              Renewal submitted, but the payment schedule was not updated — the
              new amount has not been applied. Check and set it on the payment
              review page.
            </span>
            <SoButton
              title="Review Payment Amount"
              type="secondary"
              data-testid="renewal-summary-review-btn"
              @click="
                $router.push({ name: RouteNames.CustomerReviewPaymentAmount })
              "
            />
          </div>
        </SoAlert>

        <SoAlert
          v-else-if="RenewAgreementMutator.isSuccess"
          type="success"
          class="mb-2"
          data-testid="renewal-summary-success"
        >
          Renewal submitted successfully.
        </SoAlert>

        <SoAlert
          v-if="submitError"
          type="error"
          class="mb-2"
          data-testid="renewal-summary-error"
        >
          {{ submitError }}
        </SoAlert>
      </SoForm>
    </SoErrorBoundary>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { useRenewalsStore } from "@/stores/renewals"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { PaymentScheduleService } from "@/services/PaymentScheduleService"
  import { FeaturesService } from "@/services/admin/FeaturesService"
  import {
    AgreementMutator,
    hasPaymentScheduleWarning,
  } from "@/mutators/agreement/AgreementMutator"
  import { RouteNames } from "@/types/router"
  import { isBackofficeApiError } from "@/api/backofficeRest/backofficeApiError"
  import { formatBackofficeErrorMessage } from "@/mutators/utils/formatBackofficeErrorMessage"
  import { formatMoney, formatSeasonalAmount } from "@/helpers/moneyHelpers"
  import {
    daysFromTodayFormatted,
    formatDate,
    formatDay,
    getMonthDays,
    isAfterToday,
  } from "@/helpers/dateHelper"
  import { getSuggestedPayment } from "@/services-handlers/getSuggestedPayment"
  import SoDialog from "@/components/SoDialog.vue"
  import SoErrorBoundary from "@/components/SoErrorBoundary.vue"
  import SoForm from "@/components/formControls/SoForm.vue"
  import SoDatePicker from "@/components/formControls/SoDatePicker.vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import SoChip from "@/components/SoChip.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import rules from "@/validators/rules"
  import { queryClient } from "@/api/utils/useQuery"
  import SoTooltip from "@/components/SoTooltip.vue"
  import dayjs from "dayjs"

  export default defineComponent({
    components: {
      SoDialog,
      SoErrorBoundary,
      SoForm,
      SoDatePicker,
      SoCheckbox,
      SoTextField,
      SoSelect,
      SoChip,
      SoButton,
      SoAlert,
      SoTooltip,
    },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
        required: true,
      },
    },
    emits: ["update:modelValue"],
    data() {
      return {
        current: useCurrentAccountStore(),
        renewals: useRenewalsStore(),
        gasChangeDate: useRenewalsStore().changeDate,
        elecChangeDate: useRenewalsStore().changeDate,
        exitFeesIncluded: useRenewalsStore().includeExitFee,
        RenewAgreementMutator: AgreementMutator.renew(),
        RouteNames,
        newScheduleAmount: 0,
        newScheduleIsSeasonal: false,
        newScheduleDayOfMonth: "",
        newScheduleFromDate: null as null | Date,
        isEdit: false,
        isValid: false,
        formatSeasonalAmount,
        formatMoney,
        isAfterToday,
        formatDay,
        formatDate,
        daysFromTodayFormatted,
        rules,
        isCoolingOffPeriodApproved: false,
        isCustomerConsentApproved: false,
        submitError: null as null | string,
      }
    },
    computed: {
      paymentScheduleNotApplied(): boolean {
        return hasPaymentScheduleWarning(this.RenewAgreementMutator.data)
      },
      value: {
        get() {
          return this.modelValue
        },
        set(value: boolean) {
          this.$emit("update:modelValue", value)
        },
      },
      showElecVatRateChangeInfo(): boolean {
        return (
          FeaturesService.isFeatureActive("SHOW_ELEC_VAT_RATE_CHANGE_INFO") ??
          false
        )
      },
      currentTariffCodes() {
        return Object.values(this.renewals.meterpointTariffs).map(
          (tariff) => tariff.currentTariff?.code,
        )
      },
      newAgreements() {
        return Object.values(this.renewals.selectedTariffs).filter(
          (tariff) => !this.currentTariffCodes.includes(tariff.code),
        )
      },
      currentSchedule() {
        return PaymentScheduleService.getCurrentSchedule(
          this.current.accountNumber,
        ).data
      },
      futureSchedules() {
        return PaymentScheduleService.getFutureSchedules(
          this.current.accountNumber,
        )
      },
      dayOfMonthOptions() {
        return getMonthDays().map(String)
      },
      firstPaymentDate() {
        if (!this.newScheduleFromDate) return dayjs(this.elecChangeDate)
        const scheduleFrom = this.newScheduleFromDate
        const firstDate = new Date(
          scheduleFrom.getFullYear(),
          scheduleFrom.getMonth(),
          Number(this.newScheduleDayOfMonth),
        )
        if (scheduleFrom.getDate() > Number(this.newScheduleDayOfMonth)) {
          firstDate.setMonth(firstDate.getMonth() + 1)
        }
        return dayjs(firstDate)
      },
      paymentScheduleFromDate() {
        // Omitted rather than guessed when unset: the gateway falls back to the
        // earliest agreement start date, and a wrong from date shifts the
        // customer's schedule start.
        return this.newScheduleFromDate
          ? dayjs(this.newScheduleFromDate)
          : undefined
      },
      renewalAgreements() {
        return Object.entries(this.renewals.selectedTariffs).map(
          ([meterpointId, tariff]) => {
            const meterpoint =
              this.renewals.meterpointTariffs[meterpointId].meterpoint
            return {
              meterPointIdentifier: meterpoint.identifier,
              productCode: tariff.code,
              startDate: meterpoint.isElectricity
                ? dayjs(this.elecChangeDate)
                : dayjs(this.gasChangeDate),
            }
          },
        )
      },
      suggestedPaymentAmount() {
        return getSuggestedPayment(this.current.accountNumber, true, true)
      },
      coolingOffEndDate() {
        return dayjs().add(14, "days")
      },
    },
    watch: {
      currentSchedule() {
        // Success clears the query cache, so the schedules refetch and land here.
        // The form is read-only by then (the submit button is disabled), so keep
        // it showing what was submitted rather than rebuilding the message from
        // the outgoing schedule's payment day and a mid-refetch suggested amount.
        if (this.RenewAgreementMutator.isSuccess) return
        this.initNewPaymentScheduleData()
      },
      gasChangeDate(value: Date) {
        if (dayjs(value).isBefore(dayjs(), "day")) return
        const latestDate =
          this.elecChangeDate && this.getLaterDate(value, this.elecChangeDate)
        this.newScheduleFromDate = latestDate
        this.renewals.changeDate = latestDate
      },
      elecChangeDate(value: Date) {
        if (dayjs(value).isBefore(dayjs(), "day")) return
        const latestDate =
          this.gasChangeDate && this.getLaterDate(value, this.gasChangeDate)
        this.newScheduleFromDate = latestDate
        this.renewals.changeDate = latestDate
      },
      value(val) {
        if (!val && this.RenewAgreementMutator.isSuccess) {
          this.clear()
        }
      },
    },
    mounted() {
      if (this.currentSchedule) {
        this.initNewPaymentScheduleData()
      }
    },
    methods: {
      initNewPaymentScheduleData() {
        if (!this.currentSchedule) return

        this.newScheduleAmount = this.suggestedPaymentAmount?.data
        this.newScheduleIsSeasonal = this.currentSchedule.isSeasonal
        this.newScheduleDayOfMonth = this.currentSchedule.nextPaymentDt
          .date()
          .toString()
        this.newScheduleFromDate = this.renewals.changeDate
        this.gasChangeDate = this.renewals.changeDate
        this.elecChangeDate = this.renewals.changeDate
      },
      getLaterDate(dt1: Date, dt2: Date) {
        return dt1 > dt2 ? dt1 : dt2
      },
      handleSave() {
        if (this.isValid === false) this.isEdit = false
      },
      clear() {
        void queryClient?.invalidateQueries()
      },
      async submitRenewal() {
        this.submitError = null
        try {
          await this.RenewAgreementMutator.mutateAsync({
            creditExitFee:
              this.renewals.exitFeeTotal === 0 && !this.exitFeesIncluded,
            paymentSchedule: {
              amount: Number(this.newScheduleAmount),
              seasonal: this.newScheduleIsSeasonal,
              estimatedAnnualCost: this.renewals.adjustedAnnualCost,
              firstPaymentDate: this.firstPaymentDate,
              paymentScheduleFromDate: this.paymentScheduleFromDate,
            },
            newAgreements: this.renewalAgreements,
          })
        } catch (error) {
          // A failed renewal rejects with BackofficeApiError. Catch it here so the
          // rejected promise doesn't escape the @click handler into SoErrorBoundary
          // (which would show a generic "Failed to render content"); surface the real
          // gateway message in the in-modal error alert instead.
          let message = ""
          if (isBackofficeApiError(error)) {
            message = formatBackofficeErrorMessage(error.userMessage)
          } else if (error instanceof Error) {
            message = error.message
          }
          this.submitError = message || "Failed to submit renewal"
        }
      },
    },
  })
</script>

<style scoped>
  .renewal-summary-modal__customer-consent-list {
    list-style: inside;
  }
</style>
